import Grid from '@mui/material/Grid';
import MixturesDetailPanel from 'components/DetailPanels/MixturesDetailPanel';
import RMProductsDetailPanel from 'components/DetailPanels/RMProductsDetailPanel';
import ShipmentsDetailPanel from 'components/DetailPanels/ShipmentsDetailPanel';
import BaseTable from 'components/Tables/BaseTable';
import { alertError } from 'ducks/alerts';
import { actions } from 'ducks/rawMaterials';
import { setContextSearchRM } from 'ducks/ui';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { filterByFieldSelector, groupBy, mixtureSubstancesBySubstance, shipmentsByRawMaterial } from 'selectors/generic';
import theme from 'source/theme';
import { POLIMER, SUBSTANCE } from 'utils/constants/coreConstants';
import tableIcons from 'utils/icons/materialTableIcons';
import columns from 'utils/tables/columns/inventory/substancePolimer';
import substancesPolimersValidator from 'utils/tables/validators/substancesPolimers';
import { currentUser } from 'selectors/currentUser';

const Component = props => {
    const {
        compositionByRawMaterial, contextSearch, mixturesBySubstance, productsById,
        rawMaterials, rawMaterialsById, shipmentsOfRawMaterial, suppliersById, user
    } = props;
    const { alertError, create, remove, setSearch, update } = props;

    // `rm.raw_material_suppliers` directly, or through any mixture the
    // substance is part of - the pair of questions both the toolbar filter and
    // the shipments panel ask.
    const hasShipment = rawMaterial => {
        const hasDirectShipment = (shipmentsOfRawMaterial[rawMaterial.id] || []).length > 0;
        const hasIndirectShipment =
            (mixturesBySubstance[rawMaterial.id] || []).map(interMixture =>
                (shipmentsOfRawMaterial[interMixture.mixture_id] || []).length > 0
            ).some(t => t);
        return { hasDirectShipment, hasIndirectShipment };
    };

    const tableRef = React.useRef(null);

    const [loading, setLoading] = useState(false);
    const [shipmentFilter, setShipmentFilter] = useState(null);
    const [productFilter, setProductFilter] = useState(null);


    let filteredRawMaterials = rawMaterials;

    let shipmentFilterIcon = tableIcons.Shipments;
    if (shipmentFilter !== null) {
        shipmentFilterIcon = tableIcons.ShipmentsStyled({ htmlColor: theme.palette[shipmentFilter ? 'success' : 'error'].main });
        filteredRawMaterials = filteredRawMaterials.filter(rawMaterial => {
            const { hasDirectShipment, hasIndirectShipment } = hasShipment(rawMaterial);
            return (hasDirectShipment || hasIndirectShipment) === shipmentFilter;
        });
    }

    let productFilterIcon = tableIcons.Products;
    if (productFilter !== null) {
        productFilterIcon = tableIcons.ProductsStyled({ htmlColor: theme.palette[productFilter ? 'success' : 'error'].main });
        filteredRawMaterials = filteredRawMaterials.filter(rawMaterial => {
            const products = compositionByRawMaterial[rawMaterial.id] || [];
            return productFilter ? products.length > 0 : products.length === 0;
        });
    }

    return (
        <Grid size={12}>
            <BaseTable
                tableRef={tableRef}
                columns={columns(user)}
                data={filteredRawMaterials}
                isLoading={loading}
                onSearchChange={text => setSearch(text)}
                onRowClick={() => null}
                options={{
                    searchText: contextSearch
                }}
                actions={[
                    {
                        isFreeAction: true,
                        icon: shipmentFilterIcon,
                        tooltip: 'Filtrar Fornecimentos',
                        onClick: () => {
                            if (shipmentFilter === true) {
                                setShipmentFilter(null);
                            } else if (shipmentFilter === false) {
                                setShipmentFilter(true);
                            } else {
                                setShipmentFilter(false);
                            }
                        }
                    },
                    {
                        isFreeAction: true,
                        icon: productFilterIcon,
                        tooltip: 'Filtrar Produtos',
                        onClick: () => {
                            if (productFilter === true) {
                                setProductFilter(null);
                            } else if (productFilter === false) {
                                setProductFilter(true);
                            } else {
                                setProductFilter(false);
                            }
                        }
                    }
                ]}
                editable={{
                    onRowAdd: newData => {
                        const validation = substancesPolimersValidator(newData, rawMaterials);
                        validation.cas = JSON.stringify(validation.cas);
                        validation.ec = JSON.stringify(validation.ec);
                        if (validation.error) {
                            alertError(validation.error);
                            return Promise.reject();
                        } else {
                            setLoading(false);
                            return create(validation)
                                .catch(e => alert(e))
                                .finally(() => setLoading(false));
                        }
                    },
                    onRowUpdate: newData => {
                        const validation = substancesPolimersValidator(newData, rawMaterials);
                        validation.cas = JSON.stringify(validation.cas);
                        validation.ec = JSON.stringify(validation.ec);
                        //validation.hazardous = JSON.stringify(validation.hazardous);
                        if (validation.error) {
                            alertError(validation.error);
                            return Promise.reject();
                        } else {
                            setLoading(true);
                            return update(newData.id, validation)
                                .catch(e => alert(e))
                                .finally(() => setLoading(false));
                        }
                    },
                    onRowDelete: oldData => {
                        const inMixtures = mixturesBySubstance[oldData.id] || [];
                        if ((oldData.code !== null && oldData.code !== '') || inMixtures.length > 0) {
                            let actionsPending = ['Por favor apague a seguinte informação associada a esta Substância/Polímero:'];
                            if (oldData.code !== null && oldData.code !== '') {
                                actionsPending.push('- Substância/Polímero em uso como Matéria Prima.');
                            }
                            if (inMixtures.length > 0) {
                                actionsPending.push('- Substância/Polímero presente em Misturas.');
                            }
                            alertError(actionsPending);
                            return Promise.resolve();
                        } else {
                            setLoading(true);
                            return remove(oldData.id)
                                .catch(e => alert(e))
                                .finally(() => setLoading(false));
                        }
                    }
                }}
                detailPanel={[
                    rowData =>
                        ({
                            icon: tableIcons.Mixtures,
                            disabled: (mixturesBySubstance[rowData.id] || []).length === 0,
                            tooltip: 'Misturas',
                            render: () => <MixturesDetailPanel rowData={mixturesBySubstance[rowData.id] || []}
                                                               data={rawMaterialsById}
                                                               title={'Misturas'}/>
                        }),
                    rowData => {
                        const { hasDirectShipment, hasIndirectShipment } = hasShipment(rowData);

                        return ({
                            icon: tableIcons.ShipmentsStyled({ htmlColor: theme.palette[hasDirectShipment || hasIndirectShipment ? 'success' : 'error'].main }),
                            disabled: !hasDirectShipment && !hasIndirectShipment,
                            tooltip: 'Fornecimentos',
                            render: () => <ShipmentsDetailPanel rowData={rowData} suppliersById={suppliersById}
                                                                rawMaterialsById={rawMaterialsById}
                                                                title={'Fornecimentos'}/>
                        });
                    },
                    rowData =>
                        ({
                            icon: tableIcons.ProductsStyled({ htmlColor: theme.palette[(compositionByRawMaterial[rowData.id] || []).length ? 'success' : 'error'].main }),
                            disabled: (compositionByRawMaterial[rowData.id] || []).length === 0,
                            tooltip: 'Produtos',
                            render: () => <RMProductsDetailPanel
                                rowData={rowData}
                                productsById={productsById}
                                rawMaterialsById={rawMaterialsById}
                                title={'Produtos'}
                            />
                        })
                ]}
            />
        </Grid>
    );
};

const makeMapStateToProps = () => {
    const filteredRawMaterials = filterByFieldSelector('rawMaterials', 'type', [SUBSTANCE, POLIMER]);
    // `rm.products` was the m2m through product_raw_materials, so one entry per
    // composition row - which is what its length counted.
    const compositionByRawMaterial = groupBy('productRawMaterials', 'raw_material_id');
    const mapStateToProps = (state, props) => ({
        compositionByRawMaterial: compositionByRawMaterial(state),
        contextSearch: state.ui.context.searchRM,
        mixturesBySubstance: mixtureSubstancesBySubstance(state),
        productsById: state.products.models,
        shipmentsOfRawMaterial: shipmentsByRawMaterial(state),
        rawMaterials: filteredRawMaterials(state),
        rawMaterialsById: state.rawMaterials.models,
        suppliersById: state.suppliers.models,
        user: currentUser(state)
    });
    return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
    alertError: error => dispatch(alertError(error)),
    create: data => dispatch(actions.createPromise(data)),
    remove: id => dispatch(actions.removePromise(id)),
    update: (id, data) => dispatch(actions.updatePromise(id, data)),
    setSearch: search => dispatch(setContextSearchRM(search))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(Component);
